import { revokeLlmGatewayKey } from "@app/deploy";
import { selectOrganizationSelectedId } from "@app/organizations";
import {
  useDispatch,
  useLoader,
  useLoaderSuccess,
  useSelector,
} from "@app/react";
import type { DeployLlmGatewayKey } from "@app/types";
import { BannerMessages } from "@app/ui/shared/banner";
import { Button } from "@app/ui/shared/button";
import { FormGroup } from "@app/ui/shared/form-group";
import { Input } from "@app/ui/shared/input";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeading,
} from "@app/ui/shared/modal";
import { ModalWrapper } from "@app/ui/shared/modal-portal";
import { useEffect, useState } from "react";

interface RevokeLlmGatewayKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  llmGatewayKey: DeployLlmGatewayKey | null;
}

/* Render this at page level rather than inside a table cell: ag-grid's CSS
 * transform creates a stacking context that swallows fixed-position overlays.
 */
export const RevokeLlmGatewayKeyModal = ({
  isOpen,
  onClose,
  llmGatewayKey,
}: RevokeLlmGatewayKeyModalProps) => {
  const dispatch = useDispatch();
  const orgId = useSelector(selectOrganizationSelectedId);
  const loader = useLoader(revokeLlmGatewayKey);
  const [confirm, setConfirm] = useState("");

  useLoaderSuccess(loader, onClose);

  useEffect(() => {
    if (!isOpen) return;
    setConfirm("");
  }, [isOpen]);

  if (!isOpen || !llmGatewayKey) return null;

  return (
    <ModalWrapper>
      <Modal isOpen={isOpen} onClose={onClose} className="min-w-[500px]">
        <ModalHeading title="Revoke LLM Gateway Key" onClose={onClose} />
        <ModalBody>
          <p className="text-gray-700 mb-4">
            This will permanently revoke <strong>{llmGatewayKey.handle}</strong>
            . Any applications using this key will immediately lose access to
            the LLM Gateway. This action cannot be undone.
          </p>
          <FormGroup
            label={`Type "${llmGatewayKey.handle}" to confirm`}
            htmlFor="revoke-confirm"
          >
            <Input
              id="revoke-confirm"
              type="text"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder={llmGatewayKey.handle}
            />
          </FormGroup>

          <BannerMessages className="mt-4" {...loader} />
        </ModalBody>
        <ModalFooter
          actions={[
            <Button key="cancel" variant="white" onClick={onClose}>
              Cancel
            </Button>,
            <Button
              key="revoke"
              variant="delete"
              disabled={confirm !== llmGatewayKey.handle}
              isLoading={loader.isLoading}
              onClick={() =>
                dispatch(revokeLlmGatewayKey({ id: llmGatewayKey.id, orgId }))
              }
            >
              Revoke Key
            </Button>,
          ]}
        />
      </Modal>
    </ModalWrapper>
  );
};
